import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  getTrainingPrograms, createTrainingProgram, getTrainingSessions, createTrainingSession, 
  getUsers, getTrainingSession, addSessionParticipant, removeSessionParticipant,
  updateSessionAttendance, updateSessionStatus, recordCompletion, downloadTrainingCertificate
} from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, GraduationCap, Calendar, Users, ChevronLeft, Clock, MapPin,
  User, CheckCircle, XCircle, Play, BookOpen, Award, Target, UserPlus,
  UserMinus, ClipboardCheck, AlertCircle, Download
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const TRAINING_TYPES = [
  { value: 'initial', label: 'Initial Training', label_ar: 'تدريب أولي' },
  { value: 'recurrent', label: 'Recurrent Training', label_ar: 'تدريب متكرر' },
  { value: 'specialized', label: 'Specialized Training', label_ar: 'تدريب متخصص' },
];

const SESSION_STATUSES = [
  { value: 'scheduled', label: 'Scheduled', label_ar: 'مجدول', color: 'bg-blue-900/30 text-blue-400 border-blue-900/50', icon: Calendar },
  { value: 'in_progress', label: 'In Progress', label_ar: 'قيد التنفيذ', color: 'bg-yellow-900/30 text-yellow-400 border-yellow-900/50', icon: Play },
  { value: 'completed', label: 'Completed', label_ar: 'مكتمل', color: 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50', icon: CheckCircle },
  { value: 'cancelled', label: 'Cancelled', label_ar: 'ملغى', color: 'bg-slate-800 text-slate-400 border-slate-700', icon: XCircle },
];

const ATTENDANCE_STATUSES = [
  { value: 'present', label: 'Present', color: 'bg-emerald-600 text-white' },
  { value: 'absent', label: 'Absent', color: 'bg-red-600 text-white' },
  { value: 'excused', label: 'Excused', color: 'bg-yellow-600 text-white' },
];

const USER_ROLES = [
  { value: 'safety_manager', label: 'Safety Manager' },
  { value: 'safety_officer', label: 'Safety Officer' },
  { value: 'investigator', label: 'Investigator' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'employee', label: 'Employee' },
];

export default function TrainingPage() {
  const { t } = useTranslation();
  const { api, user, token } = useAuth();
  const { language, isRTL } = useLanguage();
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateProgram, setShowCreateProgram] = useState(false);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionDetail, setShowSessionDetail] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});

  const [programForm, setProgramForm] = useState({
    title: '', title_ar: '', description: '', description_ar: '',
    training_type: 'initial', duration_hours: 4, target_roles: [], competencies: []
  });

  const [sessionForm, setSessionForm] = useState({
    program_id: '', scheduled_date: new Date().toISOString().split('T')[0],
    location: '', location_ar: '', instructor: '', max_participants: 20
  });

  const [competencyInput, setCompetencyInput] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [programsRes, sessionsRes, usersRes] = await Promise.all([
        getTrainingPrograms(api),
        getTrainingSessions(api),
        getUsers(api)
      ]);
      setPrograms(programsRes.data || []);
      setSessions(sessionsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch training data:', error);
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      const response = await getTrainingSession(api, sessionId);
      setSelectedSession(response.data);
      setAttendanceData(response.data.attendance || {});
    } catch (error) {
      console.error('Failed to fetch session details:', error);
    }
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    try {
      await createTrainingProgram(api, programForm);
      toast.success('Training program created successfully');
      setShowCreateProgram(false);
      resetProgramForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create program:', error);
      toast.error('Failed to create program');
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      await createTrainingSession(api, {
        ...sessionForm,
        scheduled_date: new Date(sessionForm.scheduled_date).toISOString()
      });
      toast.success('Training session scheduled successfully');
      setShowCreateSession(false);
      resetSessionForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create session:', error);
      toast.error('Failed to schedule session');
    }
  };

  const handleAddParticipant = async (userId) => {
    if (!selectedSession) return;
    try {
      await addSessionParticipant(api, selectedSession.id, userId);
      toast.success('Participant added');
      fetchSessionDetails(selectedSession.id);
      fetchData();
    } catch (error) {
      console.error('Failed to add participant:', error);
      toast.error(error.response?.data?.detail || 'Failed to add participant');
    }
  };

  const handleRemoveParticipant = async (userId) => {
    if (!selectedSession) return;
    try {
      await removeSessionParticipant(api, selectedSession.id, userId);
      toast.success('Participant removed');
      fetchSessionDetails(selectedSession.id);
      fetchData();
    } catch (error) {
      console.error('Failed to remove participant:', error);
      toast.error('Failed to remove participant');
    }
  };

  const handleAttendanceChange = (userId, status) => {
    setAttendanceData(prev => ({ ...prev, [userId]: status }));
  };

  const handleSaveAttendance = async () => {
    if (!selectedSession) return;
    try {
      await updateSessionAttendance(api, selectedSession.id, attendanceData);
      toast.success('Attendance saved');
      fetchSessionDetails(selectedSession.id);
    } catch (error) {
      console.error('Failed to save attendance:', error);
      toast.error('Failed to save attendance');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedSession) return;
    try {
      await updateSessionStatus(api, selectedSession.id, newStatus);
      toast.success(`Session ${newStatus === 'completed' ? 'completed' : 'started'}`);
      fetchSessionDetails(selectedSession.id);
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleMarkComplete = async (userId) => {
    if (!selectedSession) return;
    try {
      await recordCompletion(api, selectedSession.id, userId, 100);
      toast.success('Completion recorded');
      fetchSessionDetails(selectedSession.id);
    } catch (error) {
      console.error('Failed to record completion:', error);
      toast.error('Failed to record completion');
    }
  };

  const handleDownloadCertificate = async (userId) => {
    if (!selectedSession || !token) return;
    try {
      const response = await downloadTrainingCertificate(token, selectedSession.id, userId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${selectedSession.session_number}_${userId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded successfully');
    } catch (error) {
      console.error('Failed to download certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to download certificate');
    }
  };

  const resetProgramForm = () => {
    setProgramForm({
      title: '', title_ar: '', description: '', description_ar: '',
      training_type: 'initial', duration_hours: 4, target_roles: [], competencies: []
    });
    setCompetencyInput('');
  };

  const resetSessionForm = () => {
    setSessionForm({
      program_id: '', scheduled_date: new Date().toISOString().split('T')[0],
      location: '', location_ar: '', instructor: '', max_participants: 20
    });
  };

  const addCompetency = () => {
    if (competencyInput.trim() && !programForm.competencies.includes(competencyInput.trim())) {
      setProgramForm({ ...programForm, competencies: [...programForm.competencies, competencyInput.trim()] });
      setCompetencyInput('');
    }
  };

  const removeCompetency = (comp) => {
    setProgramForm({ ...programForm, competencies: programForm.competencies.filter(c => c !== comp) });
  };

  const toggleRole = (role) => {
    const newRoles = programForm.target_roles.includes(role)
      ? programForm.target_roles.filter(r => r !== role)
      : [...programForm.target_roles, role];
    setProgramForm({ ...programForm, target_roles: newRoles });
  };

  const openSessionDetail = async (session) => {
    setSelectedSession(session);
    setAttendanceData(session.attendance || {});
    setShowSessionDetail(true);
    await fetchSessionDetails(session.id);
  };

  const getStatusInfo = (status) => SESSION_STATUSES.find(s => s.value === status) || SESSION_STATUSES[0];
  const getTypeInfo = (type) => TRAINING_TYPES.find(t => t.value === type) || TRAINING_TYPES[0];
  const getProgramById = (id) => programs.find(p => p.id === id);
  const getUserById = (id) => users.find(u => u.id === id);

  // Stats
  const totalPrograms = programs.length;
  const activePrograms = programs.filter(p => p.is_active).length;
  const upcomingSessions = sessions.filter(s => s.status === 'scheduled').length;
  const completedSessions = sessions.filter(s => s.status === 'completed').length;

  const canManage = ['admin', 'safety_manager', 'trainer'].includes(user?.role);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="training-title">
            {t('training.title')}
          </h1>
          <p className="text-slate-400 mt-1">Manage training programs, sessions, and attendance tracking</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Programs</p>
                <p className="text-2xl font-bold text-white">{totalPrograms}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-sky-600/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-sky-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active Programs</p>
                <p className="text-2xl font-bold text-emerald-400">{activePrograms}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Upcoming Sessions</p>
                <p className="text-2xl font-bold text-blue-400">{upcomingSessions}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Completed</p>
                <p className="text-2xl font-bold text-violet-400">{completedSessions}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-violet-600/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="sessions" className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="sessions" className="data-[state=active]:bg-sky-600">
            <Calendar className="w-4 h-4 mr-2" />
            {t('training.sessions')}
          </TabsTrigger>
          <TabsTrigger value="programs" className="data-[state=active]:bg-sky-600">
            <BookOpen className="w-4 h-4 mr-2" />
            {t('training.programs')}
          </TabsTrigger>
        </TabsList>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => setShowCreateSession(true)} 
              className="bg-sky-600 hover:bg-sky-700"
              disabled={programs.length === 0}
              data-testid="new-session-btn"
            >
              <Plus className="w-4 h-4 mr-2" />Schedule Session
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-8 h-8" />
            </div>
          ) : sessions.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="py-12 text-center">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-700" />
                <h3 className="text-xl font-semibold text-white mb-2">No Training Sessions</h3>
                <p className="text-slate-400">
                  {programs.length === 0 ? 'Create a training program first.' : 'Schedule a training session.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sessions.map((session) => {
                const statusInfo = getStatusInfo(session.status);
                const StatusIcon = statusInfo.icon;
                const program = getProgramById(session.program_id);
                const participantCount = session.participants?.length || 0;
                const attendedCount = Object.values(session.attendance || {}).filter(a => a === 'present').length;
                
                return (
                  <Card 
                    key={session.id}
                    className="bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer"
                    onClick={() => openSessionDetail(session)}
                    data-testid={`session-card-${session.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <span className="font-mono text-sm text-sky-400">{session.session_number}</span>
                        <Badge className={`${statusInfo.color} border text-xs`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {language === 'ar' ? statusInfo.label_ar : statusInfo.label}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1">{program?.title || 'Unknown Program'}</h3>
                      <p className="text-sm text-slate-500 mb-3">{program?.program_number}</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-400">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(session.scheduled_date), 'PPP')}
                        </div>
                        <div className="flex items-center gap-2 text-slate-400">
                          <MapPin className="w-4 h-4" />
                          {session.location}
                        </div>
                        <div className="flex items-center gap-2 text-slate-400">
                          <User className="w-4 h-4" />
                          {session.instructor}
                        </div>
                        <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
                          <span className="flex items-center gap-1 text-slate-500">
                            <Users className="w-3 h-3" />
                            {participantCount} / {session.max_participants}
                          </span>
                          {session.status === 'completed' && (
                            <span className="flex items-center gap-1 text-emerald-400 text-xs">
                              <CheckCircle className="w-3 h-3" />
                              {attendedCount} attended
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Programs Tab */}
        <TabsContent value="programs" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowCreateProgram(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="new-program-btn">
              <Plus className="w-4 h-4 mr-2" />{t('training.newProgram')}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-8 h-8" />
            </div>
          ) : programs.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="py-12 text-center">
                <GraduationCap className="w-16 h-16 mx-auto mb-4 text-slate-700" />
                <h3 className="text-xl font-semibold text-white mb-2">No Training Programs</h3>
                <p className="text-slate-400">Create a training program to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {programs.map((program) => {
                const typeInfo = getTypeInfo(program.training_type);
                const programSessions = sessions.filter(s => s.program_id === program.id);
                
                return (
                  <Card 
                    key={program.id}
                    className="bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer"
                    onClick={() => setSelectedProgram(program)}
                    data-testid={`program-card-${program.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <span className="font-mono text-sm text-sky-400">{program.program_number}</span>
                        <Badge className={program.is_active ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-900/50' : 'bg-slate-800 text-slate-400'}>
                          {program.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">{program.title}</h3>
                      <p className="text-sm text-slate-400 line-clamp-2 mb-3">{program.description}</p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">{language === 'ar' ? typeInfo.label_ar : typeInfo.label}</span>
                          <span className="flex items-center gap-1 text-slate-400">
                            <Clock className="w-3 h-3" />
                            {program.duration_hours}h
                          </span>
                        </div>
                        {program.competencies?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {program.competencies.slice(0, 3).map((comp, idx) => (
                              <Badge key={idx} variant="outline" className="border-slate-700 text-slate-400 text-xs">
                                {comp}
                              </Badge>
                            ))}
                            {program.competencies.length > 3 && (
                              <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
                                +{program.competencies.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                        <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-sm">
                          <span className="text-slate-500">{programSessions.length} sessions</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Session Detail Dialog with Attendance */}
      <Dialog open={showSessionDetail} onOpenChange={setShowSessionDetail}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedSession && (() => {
            const statusInfo = getStatusInfo(selectedSession.status);
            const StatusIcon = statusInfo.icon;
            const program = getProgramById(selectedSession.program_id);
            const participants = selectedSession.participants || [];
            const completionRecords = selectedSession.completion_records || {};
            
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-sky-400">{selectedSession.session_number}</span>
                      <Badge className={`${statusInfo.color} border`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusInfo.label}
                      </Badge>
                    </div>
                    {canManage && (
                      <div className="flex gap-2">
                        {selectedSession.status === 'scheduled' && (
                          <Button size="sm" onClick={() => handleStatusChange('in_progress')} className="bg-yellow-600 hover:bg-yellow-700">
                            <Play className="w-3 h-3 mr-1" />Start
                          </Button>
                        )}
                        {selectedSession.status === 'in_progress' && (
                          <Button size="sm" onClick={() => handleStatusChange('completed')} className="bg-emerald-600 hover:bg-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />Complete
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <DialogTitle className="text-white text-xl">{program?.title || 'Training Session'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Session Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date</p>
                      <p className="text-white text-sm">{format(new Date(selectedSession.scheduled_date), 'PPP')}</p>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Duration</p>
                      <p className="text-white text-sm">{program?.duration_hours || 0} hours</p>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</p>
                      <p className="text-white text-sm">{selectedSession.location}</p>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-xs text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Instructor</p>
                      <p className="text-white text-sm">{selectedSession.instructor}</p>
                    </div>
                  </div>

                  {/* Participants & Attendance */}
                  <Card className="bg-slate-800/30 border-slate-700">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-md text-white flex items-center gap-2">
                          <Users className="w-4 h-4 text-sky-400" />
                          Participants ({participants.length} / {selectedSession.max_participants})
                        </CardTitle>
                        {canManage && selectedSession.status !== 'completed' && (
                          <Button size="sm" variant="outline" onClick={() => setShowAddParticipant(true)} className="border-slate-600 text-slate-300">
                            <UserPlus className="w-3 h-3 mr-1" />Add
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {participants.length === 0 ? (
                        <div className="text-center py-6 text-slate-500">
                          <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                          <p>No participants enrolled</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {participants.map((userId) => {
                            const participant = getUserById(userId);
                            const attendance = attendanceData[userId] || 'not_marked';
                            const isCompleted = userId in completionRecords;
                            
                            return (
                              <div key={userId} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                                    <User className="w-4 h-4 text-slate-400" />
                                  </div>
                                  <div>
                                    <p className="text-white text-sm font-medium">{participant?.name || 'Unknown User'}</p>
                                    <p className="text-slate-500 text-xs">{participant?.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {canManage && selectedSession.status !== 'completed' ? (
                                    <Select value={attendance} onValueChange={(v) => handleAttendanceChange(userId, v)}>
                                      <SelectTrigger className="w-28 h-8 bg-slate-900 border-slate-700 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className="bg-slate-900 border-slate-800">
                                        <SelectItem value="not_marked">Not Marked</SelectItem>
                                        {ATTENDANCE_STATUSES.map(s => (
                                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge className={ATTENDANCE_STATUSES.find(s => s.value === attendance)?.color || 'bg-slate-600 text-white'}>
                                      {ATTENDANCE_STATUSES.find(s => s.value === attendance)?.label || 'Not Marked'}
                                    </Badge>
                                  )}
                                  {isCompleted ? (
                                    <>
                                      <Badge className="bg-emerald-600 text-white">
                                        <Award className="w-3 h-3 mr-1" />
                                        Completed
                                      </Badge>
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        onClick={(e) => { e.stopPropagation(); handleDownloadCertificate(userId); }}
                                        className="border-sky-600 text-sky-400 h-8 text-xs"
                                        data-testid={`download-cert-${userId}`}
                                      >
                                        <Download className="w-3 h-3 mr-1" />Certificate
                                      </Button>
                                    </>
                                  ) : canManage && attendance === 'present' && selectedSession.status === 'in_progress' ? (
                                    <Button size="sm" variant="outline" onClick={() => handleMarkComplete(userId)} className="border-emerald-600 text-emerald-400 h-8 text-xs">
                                      <Award className="w-3 h-3 mr-1" />Mark Complete
                                    </Button>
                                  ) : null}
                                  {canManage && selectedSession.status === 'scheduled' && (
                                    <Button size="sm" variant="ghost" onClick={() => handleRemoveParticipant(userId)} className="text-red-400 hover:text-red-300 hover:bg-red-950/30 h-8">
                                      <UserMinus className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {canManage && participants.length > 0 && selectedSession.status !== 'completed' && (
                        <div className="mt-4 pt-4 border-t border-slate-700 flex justify-end">
                          <Button onClick={handleSaveAttendance} className="bg-sky-600 hover:bg-sky-700" data-testid="save-attendance-btn">
                            <ClipboardCheck className="w-4 h-4 mr-2" />
                            Save Attendance
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Attendance Summary */}
                  {selectedSession.status === 'completed' && participants.length > 0 && (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-emerald-950/30 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-400">
                          {Object.values(attendanceData).filter(a => a === 'present').length}
                        </p>
                        <p className="text-xs text-slate-400">Present</p>
                      </div>
                      <div className="p-3 bg-red-950/30 rounded-lg text-center">
                        <p className="text-2xl font-bold text-red-400">
                          {Object.values(attendanceData).filter(a => a === 'absent').length}
                        </p>
                        <p className="text-xs text-slate-400">Absent</p>
                      </div>
                      <div className="p-3 bg-yellow-950/30 rounded-lg text-center">
                        <p className="text-2xl font-bold text-yellow-400">
                          {Object.values(attendanceData).filter(a => a === 'excused').length}
                        </p>
                        <p className="text-xs text-slate-400">Excused</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Add Participant Dialog */}
      <Dialog open={showAddParticipant} onOpenChange={setShowAddParticipant}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-sky-400" />
              Add Participant
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2 max-h-[400px] overflow-y-auto">
            {users.filter(u => !selectedSession?.participants?.includes(u.id)).map(u => (
              <div key={u.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                    <User className="w-4 h-4 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm">{u.name}</p>
                    <p className="text-slate-500 text-xs">{u.email}</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => { handleAddParticipant(u.id); }} className="bg-sky-600 hover:bg-sky-700">
                  <Plus className="w-3 h-3 mr-1" />Add
                </Button>
              </div>
            ))}
            {users.filter(u => !selectedSession?.participants?.includes(u.id)).length === 0 && (
              <p className="text-center text-slate-500 py-4">All users are already enrolled</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Program Dialog */}
      <Dialog open={showCreateProgram} onOpenChange={setShowCreateProgram}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-sky-400" />
              {t('training.newProgram')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProgram} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Title (English) *</Label>
                <Input value={programForm.title} onChange={(e) => setProgramForm({...programForm, title: e.target.value})} required className="bg-slate-950 border-slate-800 mt-1" data-testid="program-title-input" />
              </div>
              <div>
                <Label className="text-slate-300">العنوان (العربية)</Label>
                <Input value={programForm.title_ar} onChange={(e) => setProgramForm({...programForm, title_ar: e.target.value})} className="bg-slate-950 border-slate-800 mt-1" dir="rtl" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Description *</Label>
              <Textarea value={programForm.description} onChange={(e) => setProgramForm({...programForm, description: e.target.value})} required rows={3} className="bg-slate-950 border-slate-800 mt-1" data-testid="program-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Training Type *</Label>
                <Select value={programForm.training_type} onValueChange={(v) => setProgramForm({...programForm, training_type: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {TRAINING_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{language === 'ar' ? t.label_ar : t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">{t('training.duration')} (hours) *</Label>
                <Input type="number" min="0.5" step="0.5" value={programForm.duration_hours} onChange={(e) => setProgramForm({...programForm, duration_hours: parseFloat(e.target.value)})} className="bg-slate-950 border-slate-800 mt-1" data-testid="program-duration-input" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Target Roles</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {USER_ROLES.map(role => (
                  <Badge key={role.value} variant={programForm.target_roles.includes(role.value) ? 'default' : 'outline'}
                    className={cn("cursor-pointer transition-colors", programForm.target_roles.includes(role.value) ? "bg-sky-600 text-white hover:bg-sky-700" : "border-slate-700 text-slate-400 hover:border-slate-600")}
                    onClick={() => toggleRole(role.value)}
                  >{role.label}</Badge>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Competencies</Label>
              <div className="flex gap-2 mt-1">
                <Input value={competencyInput} onChange={(e) => setCompetencyInput(e.target.value)} className="bg-slate-950 border-slate-800" placeholder="Add a competency..." onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCompetency())} />
                <Button type="button" onClick={addCompetency} variant="outline" className="border-slate-700 text-slate-300">Add</Button>
              </div>
              {programForm.competencies.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {programForm.competencies.map((comp, idx) => (
                    <Badge key={idx} variant="outline" className="border-sky-600 text-sky-400">
                      {comp}<button type="button" onClick={() => removeCompetency(comp)} className="ml-1 hover:text-red-400">×</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-program-btn">{t('common.create')}</Button>
              <Button type="button" variant="outline" onClick={() => { setShowCreateProgram(false); resetProgramForm(); }} className="border-slate-700 text-slate-300">{t('common.cancel')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Session Dialog */}
      <Dialog open={showCreateSession} onOpenChange={setShowCreateSession}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-sky-400" />
              Schedule Training Session
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSession} className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Training Program *</Label>
              <Select value={sessionForm.program_id} onValueChange={(v) => setSessionForm({...sessionForm, program_id: v})}>
                <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="session-program-select"><SelectValue placeholder="Select a program" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {programs.filter(p => p.is_active).map(p => (<SelectItem key={p.id} value={p.id}>{p.program_number} - {p.title}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Scheduled Date *</Label>
                <Input type="date" value={sessionForm.scheduled_date} onChange={(e) => setSessionForm({...sessionForm, scheduled_date: e.target.value})} required className="bg-slate-950 border-slate-800 mt-1" data-testid="session-date-input" />
              </div>
              <div>
                <Label className="text-slate-300">Max Participants</Label>
                <Input type="number" min="1" value={sessionForm.max_participants} onChange={(e) => setSessionForm({...sessionForm, max_participants: parseInt(e.target.value)})} className="bg-slate-950 border-slate-800 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Location *</Label>
                <Input value={sessionForm.location} onChange={(e) => setSessionForm({...sessionForm, location: e.target.value})} required className="bg-slate-950 border-slate-800 mt-1" placeholder="e.g., Training Room A" data-testid="session-location-input" />
              </div>
              <div>
                <Label className="text-slate-300">الموقع (العربية)</Label>
                <Input value={sessionForm.location_ar} onChange={(e) => setSessionForm({...sessionForm, location_ar: e.target.value})} className="bg-slate-950 border-slate-800 mt-1" dir="rtl" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Instructor *</Label>
              <Input value={sessionForm.instructor} onChange={(e) => setSessionForm({...sessionForm, instructor: e.target.value})} required className="bg-slate-950 border-slate-800 mt-1" placeholder="Instructor name" data-testid="session-instructor-input" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700" disabled={!sessionForm.program_id} data-testid="submit-session-btn">Schedule Session</Button>
              <Button type="button" variant="outline" onClick={() => { setShowCreateSession(false); resetSessionForm(); }} className="border-slate-700 text-slate-300">{t('common.cancel')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Program Detail Dialog */}
      <Dialog open={!!selectedProgram} onOpenChange={() => setSelectedProgram(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          {selectedProgram && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-sky-400">{selectedProgram.program_number}</span>
                  <Badge className={selectedProgram.is_active ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-900/50' : 'bg-slate-800 text-slate-400'}>{selectedProgram.is_active ? 'Active' : 'Inactive'}</Badge>
                </div>
                <DialogTitle className="text-white text-xl">{selectedProgram.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-slate-300">{selectedProgram.description}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-sm text-slate-500">Training Type</p>
                    <p className="text-white capitalize">{getTypeInfo(selectedProgram.training_type).label}</p>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-sm text-slate-500">Duration</p>
                    <p className="text-white">{selectedProgram.duration_hours} hours</p>
                  </div>
                </div>
                {selectedProgram.target_roles?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Target Roles</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedProgram.target_roles.map((role, idx) => (
                        <Badge key={idx} variant="outline" className="border-slate-700 text-slate-300 capitalize">{role.replace('_', ' ')}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedProgram.competencies?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Competencies Covered</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedProgram.competencies.map((comp, idx) => (
                        <Badge key={idx} className="bg-sky-900/30 text-sky-400 border border-sky-900/50">
                          <Target className="w-3 h-3 mr-1" />{comp}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
